import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Layers, Clock, CheckCircle2, PauseCircle, CalendarPlus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { MyTargetCard } from '../../components/MyTargetCard';

const statusConfig = {
  Running: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Clock },
  Completed: { color: 'bg-slate-100 text-slate-600 border-slate-200', icon: CheckCircle2 },
  'On Hold': { color: 'bg-amber-50 text-amber-700 border-amber-200', icon: PauseCircle },
  Upcoming: { color: 'bg-blue-50 text-blue-700 border-blue-200', icon: CalendarPlus },
};

function BatchCard({ batch, onClick }) {
  const cfg = statusConfig[batch.status] || statusConfig.Running;
  return (
    <Card
      className="border p-4 cursor-pointer hover:border-violet-300 hover:shadow-sm transition-all duration-150"
      onClick={onClick}
      data-testid={`batch-card-${batch.batch_id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <span className="text-sm font-bold text-violet-600">#{batch.batch_number}</span>
          <span className="text-sm text-slate-600 ml-2">{batch.course_name}</span>
        </div>
        <Badge variant="outline" className={`text-[9px] px-1.5 py-0.5 ${cfg.color}`}>
          {batch.status}
        </Badge>
      </div>
      <div className="flex items-center gap-4 text-xs text-slate-500">
        <span>{batch.centre_name || '-'}</span>
        <span>{batch.batch_type || ''}</span>
        {batch.timing && <span>{batch.timing}</span>}
      </div>
      <div className="flex items-center gap-4 mt-2 text-xs">
        <span className="text-slate-400">Start: <span className="text-slate-600">{batch.start_date || '-'}</span></span>
        <span className="bg-violet-50 text-violet-700 px-2 py-0.5 rounded-sm font-medium">{batch.student_count} student{batch.student_count !== 1 ? 's' : ''}</span>
        {batch.overdue_count > 0 && <span className="bg-rose-50 text-rose-700 border border-rose-200 px-2 py-0.5 rounded-sm font-medium" data-testid={`batch-overdue-count-${batch.batch_id}`}>{batch.overdue_count} overdue</span>}
      </div>
    </Card>
  );
}

function BatchSection({ title, icon: Icon, batches, navigate, emptyMsg }) {
  if (batches.length === 0) return null;
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-violet-500" />
        <h2 className="text-sm font-semibold text-slate-800">{title}</h2>
        <span className="text-xs text-slate-400">({batches.length})</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {batches.map(b => (
          <BatchCard key={b.batch_id} batch={b} onClick={() => navigate(`/trainer/batches/${b.batch_id}`)} />
        ))}
      </div>
    </div>
  );
}

export default function TrainerDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/trainer/batches')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-sm text-slate-400 text-center py-12">Failed to load batches</p>;
  }

  return (
    <div className="space-y-6" data-testid="trainer-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            Welcome, {user?.full_name || user?.name || 'Trainer'}
          </h1>
          <p className="text-sm text-slate-400 mt-0.5">
            You have <span className="font-semibold text-violet-600">{data.total_batches}</span> batch{data.total_batches !== 1 ? 'es' : ''} assigned
          </p>
        </div>
      </div>
      <div className="max-w-md"><MyTargetCard /></div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Weekday Running', count: data.weekday_running.length, color: 'text-emerald-600' },
          { label: 'Weekend Running', count: data.weekend_running.length, color: 'text-emerald-600' },
          { label: 'Upcoming', count: data.upcoming.length, color: 'text-blue-600' },
          { label: 'On Hold', count: data.on_hold.length, color: 'text-amber-600' },
          { label: 'Completed', count: data.completed.length, color: 'text-slate-500' },
        ].map(k => (
          <Card key={k.label} className="border p-3 text-center">
            <p className={`text-xl font-bold ${k.color}`}>{k.count}</p>
            <p className="text-[10px] uppercase text-slate-400 font-semibold tracking-wider mt-0.5">{k.label}</p>
          </Card>
        ))}
      </div>

      {/* Batch sections */}
      <BatchSection title="Weekday Running" icon={Clock} batches={data.weekday_running} navigate={navigate} />
      <BatchSection title="Weekend Running" icon={Clock} batches={data.weekend_running} navigate={navigate} />
      <BatchSection title="Upcoming" icon={CalendarPlus} batches={data.upcoming} navigate={navigate} />
      <BatchSection title="On Hold" icon={PauseCircle} batches={data.on_hold} navigate={navigate} />
      <BatchSection title="Completed" icon={CheckCircle2} batches={data.completed} navigate={navigate} />

      {data.total_batches === 0 && (
        <Card className="border p-12 text-center">
          <Layers className="w-12 h-12 text-slate-200 mx-auto mb-3" />
          <p className="text-sm text-slate-400">No batches assigned to you yet</p>
        </Card>
      )}
    </div>
  );
}
